import { MoreHoriz } from "@mui/icons-material";
import { IconButton, Stack } from "@mui/material";
import React from "react";

import type { ProgressState } from "../../Video/useVideo";
import ButtonSkipBack from "../ControlsCenter/ButtonSkipBack/ButtonSkipBack";
import ButtonSkipForward from "../ControlsCenter/ButtonSkipForward/ButtonSkipForward";
import ButtonAutoplay from "./ButtonAutoplay/ButtonAutoplay";
import ButtonClosedCaptions from "./ButtonClosedCaptions/ButtonClosedCaptions";
import ButtonFullscreen from "./ButtonFullscreen/ButtonFullscreen";
import ButtonLayout from "./ButtonLayout/ButtonLayout";
import ButtonPlayPause from "./ButtonPlayPause/ButtonPlayPause";
import ButtonReplay from "./ButtonReplay/ButtonReplay";
import ButtonSettings from "./ButtonSettings/ButtonSettings";
import ButtonVolume from "./ButtonVolume/ButtonVolume";
import {
  AdditionalControlsPopover,
  ButtonsContainer,
  ControlsBottomBackground,
  ControlsBottomContainer,
  LeftSideControls,
  ProgressBar,
  RightSideControls,
} from "./ControlsBottom.elements";
import VideoTimestamp from "./Timestamp/Timestamp";

type ControlsBottomProps = {
  isPlaying: boolean;
  isAutoplayEnabled: boolean;
  handleClickAutoplay: (value: boolean) => void;
  handleClickPlayPause: () => void;
  progress: ProgressState;
  handleMouseUpProgressBar: () => void;
  handleMouseDownProgressBar: () => void;
  handleMouseOutProgressBar: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleChangeProgressBar: (_: any, value: number | number[]) => void;
  handleClickReplay: () => void;
  isFullscreen: boolean;
  handleClickFullscreen: () => void;
  handleClickPIP: () => void;
  playbackRate: number;
  handleClickPlaybackRate: (value: number) => void;
  volume: number;
  handleClickMuteVolume: () => void;
  handleChangeVolumeSlider: (event: Event, value: number | number[]) => void;
  handleCommitVolumeSlider: (event: Event, value: number | number[]) => void;
  forceShowVolumeSlider: boolean;
  duration: number;
  handleClickCaptions: (value: boolean) => void;
  captionsEnabled: boolean;
  handleChangeView: () => void;
  muted: boolean;
  hasCustomDuration: boolean;
  skip: (amount: number) => void;
  captionLanguage: string;
  setCaptionLanguage: (lang: string) => void;
  availableCaptionLanguages?: { language: string; path?: string }[];
  disableSliderTransition: boolean;
  disabled?: boolean;
};

export default function ControlsBottom({
  isPlaying,
  isAutoplayEnabled,
  handleClickAutoplay,
  handleClickPlayPause,
  progress,
  handleMouseUpProgressBar,
  handleMouseDownProgressBar,
  handleMouseOutProgressBar,
  handleChangeProgressBar,
  handleClickReplay,
  isFullscreen,
  handleClickFullscreen,
  handleClickPIP,
  playbackRate,
  handleClickPlaybackRate,
  volume,
  handleClickMuteVolume,
  handleChangeVolumeSlider,
  handleCommitVolumeSlider,
  forceShowVolumeSlider,
  duration,
  handleClickCaptions,
  captionsEnabled,
  handleChangeView,
  muted,
  hasCustomDuration,
  skip,
  captionLanguage,
  setCaptionLanguage,
  availableCaptionLanguages,
  disableSliderTransition,
  disabled = false,
}: ControlsBottomProps): React.ReactElement {
  const AdditionalWrapper = Stack;

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [activeMenu, setActiveMenu] = React.useState<"captions" | "settings" | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "additional_controls" : undefined;

  const additionalControls = (
    <>
      <ButtonAutoplay isAutoplayEnabled={isAutoplayEnabled} onClickAutoplay={handleClickAutoplay} />
      <ButtonClosedCaptions
        className="captions"
        captionsEnabled={captionsEnabled}
        onClickCaptions={handleClickCaptions}
        captionLanguage={captionLanguage}
        setCaptionLanguage={setCaptionLanguage}
        availableCaptionLanguages={availableCaptionLanguages}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />
      <ButtonSettings
        className="settings"
        playbackRate={playbackRate}
        onClickPlaybackRate={handleClickPlaybackRate}
        onClickPIP={handleClickPIP}
        hasCustomDuration={hasCustomDuration}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />
      <ButtonLayout data-targetid="layout" onClickLayout={handleChangeView} />
      <ButtonFullscreen isFullscreen={isFullscreen} onClickFullscreen={handleClickFullscreen} />
    </>
  );

  return (
    <ControlsBottomContainer sx={disabled ? { pointerEvents: "none" } : undefined}>
      <ControlsBottomBackground />
      <ProgressBar
        aria-label={`video progress bar, current progress ${Math.floor(progress.playedSeconds)} seconds`}
        size="small"
        value={progress.played}
        min={0}
        max={0.999999}
        step={0.000001}
        onMouseDown={handleMouseDownProgressBar}
        onMouseUp={handleMouseUpProgressBar}
        onChange={handleChangeProgressBar}
        onMouseOut={handleMouseOutProgressBar}
        tabIndex={1}
        id="progress_bar"
        data-testid="progress_bar"
        className={disableSliderTransition ? "no-transition" : ""}
      />
      <ButtonsContainer>
        <LeftSideControls>
          <ButtonReplay onClickReplay={handleClickReplay} />
          <ButtonSkipBack skip={skip} />

          <ButtonPlayPause onClickPlayPause={handleClickPlayPause} isPlaying={isPlaying} />
          <ButtonSkipForward skip={skip} />

          <ButtonVolume
            volume={volume}
            muted={muted}
            onClickMuteVolume={handleClickMuteVolume}
            onChangeVolumeSlider={handleChangeVolumeSlider}
            onCommitVolumeSlider={handleCommitVolumeSlider}
            forceShowVolumeSlider={forceShowVolumeSlider}
          />
          <VideoTimestamp progress={progress} duration={duration} />
        </LeftSideControls>
        <RightSideControls className="rightSideControls">
          <ButtonClosedCaptions
            className="condensed"
            captionsEnabled={captionsEnabled}
            onClickCaptions={handleClickCaptions}
            captionLanguage={captionLanguage}
            setCaptionLanguage={setCaptionLanguage}
            availableCaptionLanguages={availableCaptionLanguages}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
          />
          <ButtonSettings
            className="condensed"
            playbackRate={playbackRate}
            onClickPlaybackRate={handleClickPlaybackRate}
            onClickPIP={handleClickPIP}
            hasCustomDuration={hasCustomDuration}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
          />

          <IconButton className="condensed" onClick={handleClick}>
            <MoreHoriz />
          </IconButton>

          <AdditionalControlsPopover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Stack alignItems="center" direction="row">
              {additionalControls}
            </Stack>
          </AdditionalControlsPopover>

          <AdditionalWrapper alignItems="center" direction="row" className="additional">
            {additionalControls}
          </AdditionalWrapper>
        </RightSideControls>
      </ButtonsContainer>
    </ControlsBottomContainer>
  );
}
